// Turns a <select data-combo> (multiple, or a single one-golfer select) into a single-height combo box with
// type-ahead and keyboard navigation. The original select stays in the DOM as
// the source of truth, so the form still submits its name (golfer_ids[]) and
// the page works unchanged if this never runs. Turbolinks, not Turbo.
//
// Keys: ArrowDown / ArrowUp move the highlight, Enter selects and stays put,
// Tab selects and moves on, Escape closes, Backspace on an empty input removes
// the last chip.

const ACTIVE_CLASS = 'bg-faso-blue-tint';

const GolferCombo = {
  init() {
    // Multi-selects (the round list filters) and single selects (one golfer per
    // row on the new-tournament and set-teams forms) alike.
    document.querySelectorAll('select[data-combo]').forEach((select) => {
      if (select.dataset.comboReady === 'true') return;
      select.dataset.comboReady = 'true';
      GolferCombo.enhance(select);
    });
  },

  enhance(select) {
    // A single select holds one golfer, so it is full once one is chosen.
    const single = !select.multiple;
    const max = single ? 1 : (parseInt(select.dataset.comboMax || '0', 10) || Infinity);
    const placeholder = select.dataset.comboPlaceholder || 'Type to search';
    // The blank option of an include_blank select means "nothing chosen", not a
    // golfer: it never shows as a chip or in the list.
    const options = Array.from(select.options).filter((option) => option.value !== '');
    // Selects sharing a data-combo-group can't pick the same golfer twice:
    // anyone chosen in another select of the group is left out of this list.
    const group = select.dataset.comboGroup;
    function takenElsewhere() {
      if (!group) return new Set();
      return new Set([...document.querySelectorAll(`select[data-combo-group="${group}"]`)]
        .filter((other) => other !== select)
        .flatMap((other) => [...other.selectedOptions].map((option) => option.value))
        .filter((value) => value !== ''));
    }

    let items = [];      // <li> currently rendered
    let matched = [];    // the <option>s they correspond to
    let activeIndex = -1;

    select.classList.add('hidden');

    const wrapper = document.createElement('div');
    wrapper.className = 'relative';

    const control = document.createElement('div');
    control.className =
      'flex flex-wrap items-center gap-1 w-full rounded-control border border-faso-divider bg-white px-2 py-px text-[13px] text-faso-navy min-h-[32px] focus-within:border-faso-blue';

    const input = document.createElement('input');
    input.type = 'text';
    input.autocomplete = 'off';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.className = 'flex-1 min-w-[6rem] border-0 px-0.5 py-0.5 leading-[1.4] text-[13px] text-faso-navy placeholder:text-faso-quiet focus:outline-none focus:ring-0';

    const list = document.createElement('ul');
    list.setAttribute('role', 'listbox');
    list.className =
      'absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-control border border-faso-divider bg-white py-1 shadow-lg hidden';

    control.appendChild(input);
    wrapper.appendChild(control);
    wrapper.appendChild(list);
    select.parentNode.insertBefore(wrapper, select);

    const selected = () => options.filter((option) => option.selected);
    const isOpen = () => !list.classList.contains('hidden');

    function renderTokens() {
      control.querySelectorAll('[data-token]').forEach((token) => token.remove());

      selected().forEach((option) => {
        const token = document.createElement('span');
        token.dataset.token = '';
        token.className =
          'inline-flex items-center gap-1 rounded-[4px] bg-faso-blue-tint px-2 py-0.5 text-xs font-medium text-faso-blue';
        token.textContent = option.text;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove ${option.text}`);
        remove.className = 'text-faso-blue hover:text-faso-navy';
        remove.innerHTML = '&times;';
        remove.addEventListener('click', (event) => {
          event.stopPropagation();
          // A single select falls back to its blank option, as if never chosen.
          if (single) select.value = ''; else option.selected = false;
          renderTokens();
          input.focus();
        });

        token.appendChild(remove);
        control.insertBefore(token, input);
      });

      const full = selected().length >= max;
      input.disabled = full;
      // Hidden too, not just disabled: its 6rem min-width would otherwise wrap
      // it onto a second line beside two chips and double the box's height.
      input.classList.toggle('hidden', full);
      input.placeholder = full || selected().length ? '' : placeholder;
      if (full) closeList();
    }

    function matches() {
      const term = input.value.trim().toLowerCase();
      const taken = takenElsewhere();
      return options.filter(
        (option) => !option.selected && !taken.has(option.value) && option.text.toLowerCase().includes(term)
      );
    }

    function setActive(index) {
      if (items.length === 0) {
        activeIndex = -1;
        return;
      }

      activeIndex = Math.max(0, Math.min(index, items.length - 1));

      items.forEach((item, i) => {
        const on = i === activeIndex;
        item.classList.toggle(ACTIVE_CLASS, on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) item.scrollIntoView({ block: 'nearest' });
      });
    }

    function choose(option) {
      if (!option) return;
      option.selected = true;
      input.value = '';
      renderTokens();
      closeList();
    }

    function openList() {
      if (selected().length >= max) return;

      matched = matches();
      items = [];
      list.innerHTML = '';

      if (matched.length === 0) {
        const empty = document.createElement('li');
        empty.className = 'px-3 py-2 text-[13px] text-faso-quiet';
        empty.textContent = 'No matches';
        list.appendChild(empty);
      }

      matched.forEach((option, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        item.className = 'cursor-pointer px-3 py-2 text-[13px] text-faso-navy hover:bg-faso-blue-tint';
        item.textContent = option.text;
        item.addEventListener('mousedown', (event) => {
          event.preventDefault();
          choose(option);
        });
        item.addEventListener('mousemove', () => setActive(index));
        list.appendChild(item);
        items.push(item);
      });

      list.classList.remove('hidden');
      input.setAttribute('aria-expanded', 'true');
      setActive(0);
    }

    function closeList() {
      list.classList.add('hidden');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      items = [];
      matched = [];
    }

    input.addEventListener('focus', openList);
    input.addEventListener('input', openList);
    control.addEventListener('click', () => input.focus());

    input.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen()) return openList();
          return setActive(activeIndex + 1);

        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen()) return openList();
          return setActive(activeIndex - 1);

        case 'Enter':
          if (!isOpen()) return;
          event.preventDefault();
          return choose(matched[activeIndex]);

        case 'Tab':
          // Select what is highlighted, then let focus move on as Tab normally would.
          // On a single select only if something was typed, so tabbing past an
          // empty row leaves it blank.
          if (isOpen() && matched[activeIndex] && (!single || input.value.trim() !== '')) choose(matched[activeIndex]);
          return;

        case 'Escape':
          return closeList();

        case 'Backspace': {
          if (input.value !== '') return;
          const last = selected().pop();
          if (!last) return;
          event.preventDefault();
          if (single) select.value = ''; else last.selected = false;
          renderTokens();
          openList();
          return;
        }

        default:
      }
    });

    document.addEventListener('click', (event) => {
      if (!wrapper.contains(event.target)) closeList();
    });

    renderTokens();
  }
};

document.addEventListener('DOMContentLoaded', GolferCombo.init);
document.addEventListener('turbolinks:load', GolferCombo.init);

export default GolferCombo;
